// AGC: EmptyState - Empty state component for Classic Navy Theme
// AGC: Clean messaging with gray text on white background
import React from 'react'
import { Button } from './Button'

interface EmptyStateProps {
  /** AGC: Icon to display (Lucide icon component) */
  icon?: React.ReactNode
  /** AGC: Main title text */
  title: string
  /** AGC: Description text */
  description?: string
  /** AGC: Primary action button */
  action?: {
    label: string
    onClick: () => void
    icon?: React.ReactNode
  }
  /** AGC: Secondary action link */
  secondaryAction?: {
    label: string
    onClick: () => void
  }
  /** AGC: Size variant */
  size?: 'sm' | 'md' | 'lg'
  /** AGC: Additional className */
  className?: string
}

/**
 * AGC: EmptyState - Clean empty state for Classic Navy Theme
 * AGC: Features:
 *   - Light background with subtle icon container
 *   - Dark gray text for readability
 *   - Centered layout with icon
 *   - Optional primary and secondary actions
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
  secondaryAction,
  size = 'md',
  className = '',
}) => {
  // AGC: Size variants
  const sizeClasses = {
    sm: {
      container: 'py-8',
      icon: 'w-10 h-10 mb-3',
      iconWrapper: 'w-14 h-14',
      title: 'text-sm',
      description: 'text-xs mt-1',
    },
    md: {
      container: 'py-12',
      icon: 'w-12 h-12 mb-4',
      iconWrapper: 'w-16 h-16',
      title: 'text-base',
      description: 'text-sm mt-2',
    },
    lg: {
      container: 'py-16',
      icon: 'w-16 h-16 mb-6',
      iconWrapper: 'w-24 h-24',
      title: 'text-lg',
      description: 'text-base mt-3',
    },
  }

  const styles = sizeClasses[size]

  return (
    <div className={`flex flex-col items-center text-center px-4 ${styles.container} ${className}`}>
      {/* AGC: Icon with subtle background */}
      {icon && (
        <div className={`
          flex items-center justify-center
          rounded-full
          bg-gray-100 dark:bg-slate-800
          ${styles.iconWrapper}
          mb-4
        `.replace(/\s+/g, ' ').trim()}>
          {React.cloneElement(icon as React.ReactElement, {
            className: `text-gray-400 dark:text-gray-500 ${styles.icon}`,
          })}
        </div>
      )}

      {/* AGC: Title */}
      <h3 className={`font-semibold text-gray-900 dark:text-white ${styles.title}`}>
        {title}
      </h3>

      {/* AGC: Description */}
      {description && (
        <p className={`text-gray-500 dark:text-gray-400 max-w-sm ${styles.description}`}>
          {description}
        </p>
      )}

      {/* AGC: Actions */}
      {(action || secondaryAction) && (
        <div className="flex flex-col items-center gap-2 mt-4">
          {action && (
            <Button
              variant="primary"
              size={size === 'sm' ? 'sm' : 'md'}
              onClick={action.onClick}
              icon={action.icon}
            >
              {action.label}
            </Button>
          )}
          {secondaryAction && (
            <button
              onClick={secondaryAction.onClick}
              className="text-sm text-navy-600 hover:text-navy-700 dark:text-navy-400 dark:hover:text-navy-300 font-medium transition-colors"
            >
              {secondaryAction.label}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * AGC: NoResults - Specific empty state for search/filter with no results
 */
interface NoResultsProps {
  query?: string
  onClear?: () => void
  className?: string
}

export const NoResults: React.FC<NoResultsProps> = ({
  query,
  onClear,
  className = '',
}) => {
  return (
    <EmptyState
      title="No results found"
      description={query ? `No results for "${query}"` : 'Try adjusting your search or filters'}
      secondaryAction={onClear ? { label: 'Clear search', onClick: onClear } : undefined}
      size="sm"
      className={className}
    />
  )
}

export default EmptyState
